
import { useState, useEffect } from "react";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useToast } from "@/hooks/use-toast";
import { apiFetch } from "@/lib/api-client";
import type { CreateContactResponse } from "@/dto/index";

// Form validation schema
export const contactFormSchema = z.object({
  name: z.string().min(2, { message: "Nimi on kohustuslik" }),
  email: z.string().email({ message: "Palun sisesta korrektne e-posti aadress" }),
  message: z.string().min(5, { message: "Sõnum on kohustuslik" }),
  // Honeypot field - should remain empty
  website: z.string().refine(val => val === "", {
    message: "This field should be left empty"
  }),
  // Timestamp to verify submission speed
  formStartTime: z.number()
});

export type ContactFormValues = z.infer<typeof contactFormSchema>;

// Store submission timestamps to implement rate limiting
const submissionHistory: number[] = [];
const RATE_LIMIT_WINDOW = 3600000; // 1 hour in milliseconds
const MAX_SUBMISSIONS_PER_WINDOW = 5; // Max 5 submissions per hour

export function useContactForm() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'partial-success' | 'error'>('idle');
  const { toast } = useToast();
  
  const form = useForm<ContactFormValues>({
    resolver: zodResolver(contactFormSchema),
    defaultValues: {
      name: "",
      email: "",
      message: "",
      website: "", // Honeypot field
      formStartTime: Date.now()
    },
  });

  // Cleanup old submission timestamps
  useEffect(() => {
    const cleanupTimestamps = () => {
      const now = Date.now();
      const cutoff = now - RATE_LIMIT_WINDOW;
      while (submissionHistory.length > 0 && submissionHistory[0] < cutoff) {
        submissionHistory.shift();
      }
    };

    cleanupTimestamps();
    const intervalId = setInterval(cleanupTimestamps, 60000); // Clean up every minute

    return () => clearInterval(intervalId);
  }, []);

  const checkRateLimit = (): boolean => {
    return submissionHistory.length < MAX_SUBMISSIONS_PER_WINDOW;
  };

  const onSubmit = async (data: ContactFormValues) => {
    setIsSubmitting(true);
    setSubmitStatus('idle');
    
    try {
      console.log("Form data:", data);

      // Anti-spam check 1: Honeypot field should be empty
      if (data.website) {
        console.log("Honeypot trap triggered - silently reject");
        // Fake success to confuse bots
        await new Promise(resolve => setTimeout(resolve, 1000));
        setSubmitStatus('success');
        form.reset();
        return;
      }

      // Anti-spam check 2: Submission speed (too fast is suspicious)
      const formFillTime = Date.now() - data.formStartTime;
      if (formFillTime < 3000) { // Less than 3 seconds is suspicious
        console.log("Form filled too quickly - likely bot");
        // Fake success to confuse bots
        await new Promise(resolve => setTimeout(resolve, 1500));
        setSubmitStatus('success');
        form.reset();
        return;
      }

      // Anti-spam check 3: Rate limiting
      if (!checkRateLimit()) {
        console.log("Rate limit exceeded");
        setSubmitStatus('error');
        toast({
          title: "Liiga palju päringuid",
          description: "Olete saatnud liiga palju sõnumeid. Palun proovige hiljem uuesti.",
          variant: "destructive",
        });
        return;
      }

      // Add current timestamp to submission history for rate limiting
      submissionHistory.push(Date.now());
      
      const responseData = await apiFetch<CreateContactResponse>('/api/contact', {
        method: 'POST',
        body: JSON.stringify(data),
      });
      
      console.log("Response from /api/contact:", responseData);
      
      setSubmitStatus('success');
      toast({
        title: "Sõnum saadetud",
        description: responseData.message || "Täname ühendust võtmast. Vastame esimesel võimalusel.",
      });
      
      form.reset();
    } catch (error: any) {
      console.error("Error submitting form:", error);
      setSubmitStatus('error');
      toast({
        title: "Viga",
        description: error.message || "Tekkis viga sõnumi saatmisel. Palun proovige hiljem uuesti.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    form,
    isSubmitting,
    submitStatus,
    onSubmit
  };
}
